<template>
  <transition name="append-banner-slide">
    <div v-if="appendModeState.isActive" class="append-mode-banner">
      <div class="append-mode-banner__content">
        <i class="tim-icons icon-calendar-60 append-mode-banner__icon"></i>
        <div class="append-mode-banner__label text-white">
          <div class="append-mode-banner__primary">
            Click an open slot to add to
            <strong v-if="orderNumberLabel">{{ orderNumberLabel }}</strong>
            <strong v-else>this transaction</strong>
          </div>
          <div v-if="customerLabel || balanceDueLabel" class="append-mode-banner__meta">
            <span v-if="customerLabel">{{ customerLabel }}</span>
            <span v-if="customerLabel && balanceDueLabel"> · </span>
            <span v-if="balanceDueLabel" class="append-mode-banner__balance">{{ balanceDueLabel }} due</span>
          </div>
        </div>
      </div>
      <div class="append-mode-banner__actions">
        <button type="button" class="append-mode-banner__done" @click="onDone">Done</button>
      </div>
    </div>
  </transition>
</template>

<script>
import { appendModeState, exitAppendMode } from '@/util/appendMode'
import { formatCurrency } from '@/util/currencyFormat'

export default {
  name: 'AppendModeBanner',
  computed: {
    appendModeState() {
      return appendModeState
    },
    customerLabel() {
      return (appendModeState.customerName || '').trim()
    },
    orderNumberLabel() {
      const num = appendModeState.orderNumber
      return num ? `Transaction #${num}` : ''
    },
    balanceDueLabel() {
      const due = Number(appendModeState.balanceDue || 0)
      if (due <= 0) return ''
      const group = JSON.parse(localStorage.getItem('group') || '{}')
      return formatCurrency(due, {
        currency: group.currency,
        currencySymbol: '$',
        currencyFormat: group.currency_format,
        numberFormat: group.number_format
      })
    }
  },
  methods: {
    onDone() {
      exitAppendMode()
    }
  }
}
</script>

<style lang="scss" scoped>
// Status banner — pins to the top of the page during an append session.
// Deliberately visible: opaque pink-tinted bg, primary-color glow for "this
// is a global mode change" emphasis. Sticky offset + margin keep visible
// breathing room between the banner and the page top whether scrolled
// or at rest.
.append-mode-banner {
  position: sticky;
  top: 16px;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 16px;
  margin: 0 15px 16px;
  background: rgba(40, 32, 54, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 4px;
  box-shadow: 0 0 12px rgba(225, 78, 202, 0.15);
}

.append-mode-banner__content {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.append-mode-banner__icon {
  font-size: 16px;
  color: #e14eca;
  flex-shrink: 0;
}

.append-mode-banner__label {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.append-mode-banner__primary {
  font-size: 13px;
  line-height: 1.4;
}

.append-mode-banner__primary strong {
  font-weight: 600;
}

.append-mode-banner__meta {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
}

.append-mode-banner__balance {
  color: #ffc107;
  font-weight: 600;
}

.append-mode-banner__actions {
  flex-shrink: 0;
}

.append-mode-banner__done {
  background: transparent;
  border: 1px solid rgba(225, 78, 202, 0.6);
  color: #e14eca;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(225, 78, 202, 0.18);
    border-color: #e14eca;
  }
}

.append-banner-slide-enter-active,
.append-banner-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.append-banner-slide-enter,
.append-banner-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

// Light-mode equivalent — same translucent + blur treatment so the banner
// reads as a floating layer regardless of theme.
.white-content {
  .append-mode-banner {
    background: rgba(255, 248, 253, 0.92);
  }
  .append-mode-banner__label {
    color: #344675;
  }
  .append-mode-banner__order {
    color: rgba(52, 70, 117, 0.65);
  }
}
</style>
